// The support trust-score review card (plan .ai/plans/16-trust-score-review-card): where the card is, the company's working
// score against what is published, and what each lambda's answer means in plain words. Pure, so the unit tests import it.

export type ExternalDomain = 'COMPANIES_HOUSE' | 'HMRC' | 'BANK';

export const EXTERNAL_DOMAINS: readonly ExternalDomain[] = ['COMPANIES_HOUSE', 'HMRC', 'BANK'];

// The Behavioural domains that are scored: worked out each night (Application interaction from the company's jobs, Supply
// chain from its suppliers' published scores) and published without review, so the card shows them read-only, for support to
// see why the overall moved
export type BehaviouralDomain = 'APPLICATION_INTERACTION' | 'SUPPLY_CHAIN';

export const BEHAVIOURAL_DOMAINS: readonly BehaviouralDomain[] = ['APPLICATION_INTERACTION', 'SUPPLY_CHAIN'];

export type ShownDomain = ExternalDomain | BehaviouralDomain;

// Every domain the card shows, in order
export const SHOWN_DOMAINS: readonly ShownDomain[] = [...EXTERNAL_DOMAINS, ...BEHAVIOURAL_DOMAINS];

export const DOMAIN_LABELS: Record<ShownDomain, string> = {
  COMPANIES_HOUSE: 'Companies House',
  HMRC: 'HMRC',
  BANK: 'Bank verification',
  APPLICATION_INTERACTION: 'Application interaction',
  SUPPLY_CHAIN: 'Supply chain',
};

// What the card says of each Behavioural domain: where its score comes from, and that it is published without review
export const AUTO_PUBLISHED_TEXT: Partial<Record<ShownDomain, string>> = {
  APPLICATION_INTERACTION: "Worked out each night from the company's jobs and published without review.",
  SUPPLY_CHAIN: "Worked out each night from its suppliers' published trust scores and published without review.",
};

// A category of a domain's working score, as scoring stores it
export interface CategoryRecord {
  name?: string | null;
  score?: number | null;
  reasons?: string[] | null;
}

export interface DomainRecord {
  domain?: string | null;
  score?: number | null;
  lastCalculatedAt?: string | null;
  scoreCalculatedAt?: string | null;
  killSwitchDetected?: boolean | null;
  killSwitchReason?: string | null;
  capApplied?: number | null;
  capReason?: string | null;
  categories?: CategoryRecord[] | null;
}

interface PillarRecord {
  pillar?: string | null;
  domains?: DomainRecord[] | null;
}

// The fields of a companyTrustScore record the card reads (support reads the whole record)
export interface TrustScoreRecord {
  id?: string;
  companyId?: string | null;
  overall?: number | null;
  killSwitchActive?: boolean | null;
  killSwitchReason?: string | null;
  lastCalculatedAt?: string | null;
  pillars?: PillarRecord[] | null;
  manualReview?: {
    status?: string | null;
    publishDecision?: string | null;
    reviewedAt?: string | null;
    reviewNotes?: string | null;
  } | null;
  published?: {
    publishedAt?: string | null;
    overall?: number | null;
    killSwitchActive?: boolean | null;
    killSwitchReason?: string | null;
    hmrcReviewExpired?: boolean | null;
    // What everyone sees beside the 0 while the score is suppressed
    suppressedReason?: string | null;
    pillars?: PillarRecord[] | null;
  } | null;
}

export type ReviewState = 'missing' | 'waiting' | 'reviewed';

/**
 * Where the record is: none to review, a score waiting for review, or already reviewed (an admin acted elsewhere). A suppressed
 * score whose review support reopened (trust-score/reopen-review, decision REOPENED) waits too: others still see it suppressed
 * until Publish or Suppress. One NOT_REVIEWED and still SUPPRESS (a re-score that raced a Suppress) is held by the Suppress, as
 * the lambdas read it (store.heldBySuppress), so it counts as reviewed.
 */
export function reviewState(record: TrustScoreRecord | null | undefined): ReviewState {
  if (!record || !record.lastCalculatedAt) return 'missing';
  const review = record.manualReview;
  if (review?.status !== 'NOT_REVIEWED') return 'reviewed';
  return review.publishDecision !== 'SUPPRESS' ? 'waiting' : 'reviewed';
}

/** Whether everyone else sees the score as suppressed: suppressed, or reopened for review and not yet decided (REOPENED). */
export function shownAsSuppressed(record: TrustScoreRecord | null | undefined): boolean {
  const decision = record?.manualReview?.publishDecision;
  return decision === 'SUPPRESS' || decision === 'REOPENED';
}

function findDomain(pillars: PillarRecord[] | null | undefined, domain: ShownDomain): DomainRecord | null {
  for (const pillar of pillars ?? []) {
    const found = (pillar?.domains ?? []).find((d) => d?.domain === domain);
    if (found) return found;
  }
  return null;
}

export interface DomainSide {
  score: number | null;
  scoredAt: string | null;
  killed: boolean;
  killReason: string | null;
  capApplied: number | null;
  capReason: string | null;
}

export interface DomainView {
  domain: ShownDomain;
  label: string;
  // Published without review (a Behavioural domain): support reads it, and nothing on the card acts on it
  autoPublished: boolean;
  // null when the domain has never been scored
  working: DomainSide | null;
  // null when nothing has been published for it
  published: DomainSide | null;
  categories: { name: string; score: number | null; reasons: string[] }[];
  // The working score differs from the published one in what other companies would see
  changed: boolean;
}

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

function side(domain: DomainRecord | null, scoredAt: string | null | undefined): DomainSide | null {
  if (!domain || !text(scoredAt)) return null;
  return {
    score: num(domain.score),
    scoredAt: text(scoredAt),
    killed: domain.killSwitchDetected === true,
    killReason: text(domain.killSwitchReason),
    capApplied: num(domain.capApplied),
    capReason: text(domain.capReason),
  };
}

const sameSide = (a: DomainSide | null, b: DomainSide | null) =>
  !!a &&
  !!b &&
  a.score === b.score &&
  a.killed === b.killed &&
  a.killReason === b.killReason &&
  a.capApplied === b.capApplied &&
  a.capReason === b.capReason;

/** One domain's working score beside its published one, with the categories behind the working score. */
export function domainView(record: TrustScoreRecord, domain: ShownDomain): DomainView {
  const workingDomain = findDomain(record.pillars, domain);
  const publishedDomain = findDomain(record.published?.pillars, domain);
  const autoPublished = (BEHAVIOURAL_DOMAINS as readonly ShownDomain[]).includes(domain);
  const working = side(workingDomain, workingDomain?.lastCalculatedAt);
  // A first publish snapshots a Behavioural domain the nightly run has not scored yet at its starting 100 with no score date:
  // that 100 is what others see, published when the record was
  const publishedAt =
    publishedDomain?.scoreCalculatedAt ??
    (autoPublished && num(publishedDomain?.score) !== null ? record.published?.publishedAt : null);
  const published = record.published?.publishedAt ? side(publishedDomain, publishedAt) : null;
  const categories = (workingDomain?.categories ?? []).map((c) => ({
    name: text(c?.name) ?? 'Category',
    score: num(c?.score),
    reasons: (c?.reasons ?? []).filter((r): r is string => typeof r === 'string' && r !== ''),
  }));
  return {
    domain,
    label: DOMAIN_LABELS[domain],
    autoPublished,
    working,
    published,
    categories,
    changed: !!working && !sameSide(working, published),
  };
}

// What a lambda answered inside its HTTP 200 (callLambda's res)
export interface LambdaAnswer {
  code?: unknown;
  message?: unknown;
  reason?: unknown;
  unscored?: unknown;
  running?: unknown;
  publishedAt?: unknown;
  alreadyPublished?: unknown;
  alreadySuppressed?: unknown;
  alreadyReopened?: unknown;
  requeued?: unknown;
  wasSuppressed?: unknown;
  lastCalculatedAt?: unknown;
  wentToReview?: unknown;
}

export type ActionResult =
  // The card completes its job with this outcome
  | { kind: 'done'; outcome: 'published' | 'suppressed'; message: string }
  // The score is published, but a newer one arrived: the card stays open on the newer score
  | { kind: 'requeued'; message: string }
  // Not done; reload: read the record again (it changed)
  | { kind: 'refused'; message: string; reload: boolean };

const domainList = (value: unknown): string => {
  const names = (Array.isArray(value) ? value : [])
    .filter((d): d is ExternalDomain => EXTERNAL_DOMAINS.includes(d as ExternalDomain))
    .map((d) => DOMAIN_LABELS[d]);
  if (names.length === 0) return 'every check';
  return names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const COMMON_REFUSALS: Record<string, string> = {
  FORBIDDEN: 'Only nodeStream support or an administrator can review trust scores.',
  NO_PROFILE: 'Your nodeStream profile could not be found. Switch to the nodeStream company and try again.',
  NOT_FOUND: "This company's trust score could not be found.",
};

function refused(answer: LambdaAnswer, fallback: string): ActionResult {
  const reason = typeof answer.reason === 'string' ? answer.reason : '';
  const message =
    COMMON_REFUSALS[reason] ?? (typeof answer.message === 'string' && answer.message ? answer.message : fallback);
  return { kind: 'refused', message, reload: false };
}

/**
 * Whether a publish should be followed by trust-score/behaviour-score: the publish found the record suppressed when it read it
 * (wasSuppressed, from the record itself rather than the card's copy, so a Suppress another reviewer made while the card was
 * open counts) and stored it (a 200, or the 500 PUBLISHED_UNCHECKED: stored but not checked). The nightly run scores no
 * suppressed company, so its behaviour score is out of date by the time it is published again.
 */
export function scoresBehaviourAfterPublish(record: TrustScoreRecord, answer: LambdaAnswer): boolean {
  // A publish lambda from before wasSuppressed answers without it: then the card's own copy decides
  const wasSuppressed = typeof answer.wasSuppressed === 'boolean' ? answer.wasSuppressed : shownAsSuppressed(record);
  if (!wasSuppressed) return false;
  return answer.code === 200 || answer.reason === 'PUBLISHED_UNCHECKED';
}

/**
 * A publish's result once trust-score/behaviour-score has answered (null when the call failed): a behaviour score that could
 * not be worked out now is worked out by the next nightly run, which the message says.
 */
export function withBehaviourScore(result: ActionResult, behaviour: LambdaAnswer | null): ActionResult {
  if (result.kind === 'refused' || behaviour?.code === 200) return result;
  return {
    ...result,
    message: `${result.message} Its behaviour score couldn't be updated now; tonight's run will update it.`,
  };
}

/** What trust-score/publish's answer means for the card. */
export function publishResult(answer: LambdaAnswer): ActionResult {
  if (answer.code === 200) {
    if (answer.requeued === true) {
      return {
        kind: 'requeued',
        message: 'Published, but a newer score arrived while you were reviewing. Review it now.',
      };
    }
    return { kind: 'done', outcome: 'published', message: 'Trust score published.' };
  }
  if (answer.code === 500 && answer.reason === 'PUBLISHED_UNCHECKED') {
    return {
      kind: 'refused',
      message: "The score was published, but the result couldn't be checked. The card now shows the latest record.",
      reload: true,
    };
  }
  switch (answer.reason) {
    case 'NOT_VERIFIED':
      return {
        kind: 'refused',
        message: `This score can't be published until ${domainList(answer.unscored)} ${
          Array.isArray(answer.unscored) && answer.unscored.length === 1 ? 'is' : 'are'
        } scored. You can suppress it, or leave this card open.`,
        reload: false,
      };
    case 'SCORE_CHANGED':
      return {
        kind: 'refused',
        message:
          'The score changed while this card was open. The card now shows the new score: check it before you publish.',
        reload: true,
      };
    case 'RESCORE_IN_PROGRESS':
      return { kind: 'refused', message: 'A re-check is running. Try again in a moment.', reload: true };
    case 'NO_DOMAINS':
      return {
        kind: 'refused',
        message: "This trust score record isn't complete, so it can't be published.",
        reload: false,
      };
    default:
      return refused(answer, "The score couldn't be published. Please try again.");
  }
}

/** What trust-score/suppress's answer means for the card. */
export function suppressResult(answer: LambdaAnswer): ActionResult {
  if (answer.code === 200) return { kind: 'done', outcome: 'suppressed', message: 'Trust score suppressed.' };
  return refused(answer, "The score couldn't be suppressed. Please try again.");
}

export type RescoreResult = { ok: true; message: string } | { ok: false; message: string };

/** What trust-score/reopen-review's answer means for the staff member who asked. */
export function reopenReviewResult(answer: LambdaAnswer): RescoreResult {
  if (answer.code === 200) {
    return {
      ok: true,
      message:
        answer.alreadyReopened === true
          ? 'This review is already reopened.'
          : 'Review reopened. Support will get a card to publish or suppress the score once every check is scored.',
    };
  }
  if (answer.reason === 'NOT_SUPPRESSED') {
    return { ok: false, message: "This company's trust score isn't suppressed, so there's no review to reopen." };
  }
  const reason = typeof answer.reason === 'string' ? answer.reason : '';
  const message =
    COMMON_REFUSALS[reason] ??
    (typeof answer.message === 'string' && answer.message ? answer.message : "The review couldn't be reopened.");
  return { ok: false, message };
}

const RESCORE_REFUSALS: Record<string, string> = {
  RESCORE_IN_PROGRESS: 'A Companies House re-check is already running. Try again in a moment.',
  SKIPPED: "Companies House wasn't re-checked. Try again in a moment.",
  NO_REGISTRAR: "This company isn't validated with Companies House, so it can't be re-checked.",
  NOT_FOUND: "Companies House has no record of this company's number.",
  FETCH_FAILED: "Companies House didn't answer in time. Try again in a moment.",
  STOP_RUN: 'Companies House is refusing requests for now. Try again later.',
  NO_API_KEY: "nodeStream's Companies House key is missing. Contact the nodeStream team.",
};

/** What companies-house/trust-score/rescore's answer means for the card. */
export function rescoreResult(answer: LambdaAnswer): RescoreResult {
  if (answer.code === 200) return { ok: true, message: 'Companies House re-checked. The card shows the new score.' };
  const reason = typeof answer.reason === 'string' ? answer.reason : '';
  const message =
    RESCORE_REFUSALS[reason] ??
    COMMON_REFUSALS[reason] ??
    (typeof answer.message === 'string' && answer.message ? answer.message : "Companies House couldn't be re-checked.");
  return { ok: false, message };
}

// The notes Publish and Suppress take: at most this many characters, required for Suppress
export const NOTES_MAX = 1000;

/** Why the notes can't be sent, or null when they can. */
export function notesProblem(notes: string, required: boolean): string | null {
  const trimmed = notes.trim();
  if (required && trimmed === '') return 'Say why you are suppressing this score.';
  if (trimmed.length > NOTES_MAX) return `Notes can be at most ${NOTES_MAX} characters.`;
  return null;
}

// The public reason Suppress takes: shown to everyone beside the score of 0, so short
export const PUBLIC_REASON_MAX = 80;

/** Why the public reason can't be sent, or null when it can. */
export function publicReasonProblem(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed === '') return 'Give the public reason everyone will see for this score.';
  if (trimmed.length > PUBLIC_REASON_MAX) return `The public reason can be at most ${PUBLIC_REASON_MAX} characters.`;
  return null;
}
